import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccountBalance as AccountBalanceIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { collection, getDocs, deleteDoc, doc, query, orderBy, where, updateDoc, limit } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const UserDataAnalysis = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterUserType, setFilterUserType] = useState("individual");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    loadUsersData();
  }, []);

  const loadUsersData = async () => {
    setLoading(true);
    try {
      const q = query(collection(DATABASE, "users"), orderBy("full_name", "asc"));
      const querySnapshot = await getDocs(q);
      
      let users = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().registered_date?.toDate?.() || doc.data().created_at?.toDate?.() || new Date(),
      }));

      // Filter to only show regular users (role = 1), not admins (role = 0)
      // If role field doesn't exist, assume all users are regular users
      users = users.filter(user => user.role === 1 || user.role === undefined);

      // Fetch monthly performance data for each user from monthly_closings collection
      const monthlyPerformancePromises = users.map(async (user) => {
        try {
          // Query monthly_closings collection for this user
          const monthlyQ = query(
            collection(DATABASE, "monthly_closings"),
            where("user_id", "==", user.id)
          );
          
          const monthlySnapshot = await getDocs(monthlyQ);
          console.log(`Monthly closings query for user ${user.id}:`, monthlySnapshot.docs.length, "documents found");
          
          if (!monthlySnapshot.empty) {
            // Sort by date descending manually and get the latest
            const monthlyClosings = monthlySnapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                date: data.date?.toDate ? data.date.toDate() : data.date || new Date()
              };
            });
            
            monthlyClosings.sort((a, b) => new Date(b.date) - new Date(a.date));
            const latestMonthlyData = monthlyClosings[0];
            
            // Calculate total performance from all monthly closings
            const totalPerformance = monthlyClosings.reduce((sum, closing) => {
              const performance = closing.user_performance || closing.percent || 0;
              return sum + parseFloat(performance);
            }, 0);
            
            console.log(`Found monthly closing data for user ${user.id}:`, latestMonthlyData);
            console.log(`Total performance for user ${user.id}:`, totalPerformance);
            return {
              ...user,
              user_performance: latestMonthlyData.user_performance || latestMonthlyData.percent || 0,
              total_performance: totalPerformance,
            };
          }
          
          console.log(`No monthly closing data found for user ${user.id}`);
          return { ...user, user_performance: 0 }; // Default to 0 if no monthly performance found
        } catch (error) {
          console.error(`Error fetching monthly closing data for user ${user.id}:`, error);
          return { ...user, user_performance: 0 };
        }
      });

      const usersWithPerformance = await Promise.all(monthlyPerformancePromises);
      
      setUsersData(usersWithPerformance);
    } catch (error) {
      console.error("Error loading users data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load users data",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = usersData.filter((item) => {
    const emailMatch = !filterEmail || item.email?.toLowerCase().includes(filterEmail.toLowerCase());
    
    // Fix status filtering - check status field (1 = active, 0 = inactive)
    const userStatus = item.status === 1 ? "active" : item.status === 0 ? "inactive" : "unknown";
    const statusMatch = !filterStatus || userStatus.toLowerCase().includes(filterStatus.toLowerCase());
    
    // User type filtering - check user_type field (corporate or individual)
    const userType = (item.user_type && typeof item.user_type === 'string') ? item.user_type : "individual"; // Default to individual if not specified or not a string
    const userTypeMatch = !filterUserType || userType.toLowerCase() === filterUserType.toLowerCase();
    
    return emailMatch && statusMatch && userTypeMatch;
  });

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = filteredData.map((item) => item.id);
      setSelectedItems(newSelected);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select items to delete",
      });
      return;
    }

    setItemsToDelete(selectedItems);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const deletePromises = itemsToDelete.map((itemId) =>
        deleteDoc(doc(DATABASE, "users", itemId))
      );

      await Promise.all(deletePromises);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: `${itemsToDelete.length} users deleted successfully`,
      });

      setSelectedItems([]);
      setDeleteDialogOpen(false);
      loadUsersData();
    } catch (error) {
      console.error("Error deleting users:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete selected users",
      });
    }
  };

  const getStatusChip = (item) => {
    // Check for missing required fields
    const missingFields = [];
    
    if (!item.email) missingFields.push("Email");
    if (!item.full_name) missingFields.push("Full Name");
    if (!item.phone) missingFields.push("Phone");
    if (item.status === undefined || item.status === null) missingFields.push("Status");

    if (missingFields.length > 0) {
      return <Chip label={`Missing: ${missingFields.join(", ")}`} color="error" size="small" />;
    }

    // Check account status
    if (item.status === 1) {
      return <Chip label="Active" color="success" size="small" />;
    } else if (item.status === 0) {
      return <Chip label="Inactive" color="warning" size="small" />;
    }

    return <Chip label="Valid" color="success" size="small" />;
  };

  const handleViewUserDetails = async (user) => {
    setSelectedUser(user);
    setUserDetailsOpen(true);
    
    // Load monthly closings for this user
    try {
      console.log("Loading monthly closings for user:", user.id);
      
      // Try without orderBy first to see if there's an index issue
      const monthlyQ = query(
        collection(DATABASE, "monthly_closings"),
        where("user_id", "==", user.id)
      );
      
      const monthlySnapshot = await getDocs(monthlyQ);
      console.log("Monthly closings query result:", monthlySnapshot.docs.length, "documents found");
      
      const monthlyClosings = monthlySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log("Monthly closing document:", doc.id, data);
        return {
          id: doc.id,
          ...data,
          date: data.date?.toDate ? data.date.toDate() : data.date || new Date()
        };
      });
      
      // Sort by date descending manually since we can't use orderBy
      monthlyClosings.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      // Calculate total performance and total sum in dollars
      const totalPerformance = monthlyClosings.reduce((sum, closing) => {
        const performance = closing.user_performance || closing.percent || 0;
        return sum + parseFloat(performance);
      }, 0);
      
      const totalSumDollars = monthlyClosings.reduce((sum, closing) => {
        const profitLoss = closing.profit_loss || 0;
        return sum + parseFloat(profitLoss);
      }, 0);
      
      console.log("Processed monthly closings:", monthlyClosings);
      console.log("Total performance:", totalPerformance);
      console.log("Total sum in dollars:", totalSumDollars);
      
      setSelectedUser(prev => ({
        ...prev,
        monthlyClosings: monthlyClosings,
        total_performance: totalPerformance,
        total_sum_dollars: totalSumDollars
      }));
    } catch (error) {
      console.error("Error loading monthly closings:", error);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditDialogOpen(true);
  };

  const handleSaveUserEdit = async () => {
    if (!editingUser) return;

    try {
      await updateDoc(doc(DATABASE, "users", editingUser.id), {
        full_name: editingUser.full_name,
        email: editingUser.email,
        phone: editingUser.phone,
        account_status: editingUser.account_status,
        net_balance: editingUser.net_balance,
      });

      // Update user_performance in the latest monthly closing entry
      if (editingUser.user_performance !== undefined && editingUser.user_performance !== null && editingUser.user_performance !== '') {
        const monthlyQ = query(
          collection(DATABASE, "monthly_grpahs"),
          where("userID", "==", editingUser.id),
          orderBy("graph_year", "desc"),
          orderBy("graph_month", "desc"),
          limit(1)
        );
        const monthlySnapshot = await getDocs(monthlyQ);
        if (!monthlySnapshot.empty) {
          const latestMonthlyDocRef = doc(DATABASE, "monthly_grpahs", monthlySnapshot.docs[0].id);
          await updateDoc(latestMonthlyDocRef, { total_Monthly_percantage: parseFloat(editingUser.user_performance) || 0 });
        } else {
          console.warn("No monthly closing found for user", editingUser.id, "to update performance.");
        }
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "User updated successfully",
      });

      setEditDialogOpen(false);
      setEditingUser(null);
      loadUsersData();
    } catch (error) {
      console.error("Error updating user:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update user",
      });
    }
  };

  const getAccountBalance = (item) => {
    if (item.net_balance !== undefined && item.net_balance !== null) {
      return `$${parseFloat(item.net_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return "N/A";
  };

  const getPerformancePercentage = (item) => {
    if (item.total_performance !== undefined && item.total_performance !== null) {
      return `${parseFloat(item.total_performance).toFixed(2)}%`;
    }
    return "N/A";
  };

  const getTotalSumInDollars = (item) => {
    if (item.total_sum_dollars !== undefined && item.total_sum_dollars !== null) {
      return `$${parseFloat(item.total_sum_dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return "N/A";
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        User Data Analysis
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Analyze and manage user accounts, validate user data integrity, and clean up invalid entries.
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {usersData.length}
              </Typography>
              <Typography variant="body2">Total Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {usersData.filter(item => item.status === 1).length}
              </Typography>
              <Typography variant="body2">Active Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {usersData.filter(item => item.status === 0).length}
              </Typography>
              <Typography variant="body2">Pending Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {usersData.filter(item => 
                  !item.email || !item.full_name || !item.phone
                ).length}
              </Typography>
              <Typography variant="body2">Incomplete Profiles</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Filter by Email"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="unknown">Unknown</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={filterUserType}
            onChange={(e) => setFilterUserType(e.target.value)}
            label="Filter by Type"
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="individual">Individual</MenuItem>
            <MenuItem value="corporate">Corporate</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadUsersData}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteSelected}
          disabled={selectedItems.length === 0}
        >
          Delete Selected ({selectedItems.length})
        </Button>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedItems.length > 0 && selectedItems.length < filteredData.length}
                  checked={filteredData.length > 0 && selectedItems.length === filteredData.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Account Status</TableCell>
              <TableCell>Balance</TableCell>
                  <TableCell>Total Performance %</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                        {item.full_name?.[0] || item.email?.[0] || "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {item.full_name || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.doc_id || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <EmailIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                      <Typography variant="body2">
                        {item.email || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                      <Typography variant="body2">
                        {item.phone || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status === 1 ? "Active" : item.status === 0 ? "Inactive" : "Unknown"}
                      color={
                        item.status === 1 ? "success" :
                        item.status === 0 ? "warning" : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccountBalanceIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                      <Typography variant="body2" fontWeight="bold">
                        {getAccountBalance(item)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="primary">
                      {item.total_performance !== undefined ? `${item.total_performance.toFixed(2)}%` : "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {dayjs(item.created_at).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit User">
                      <IconButton 
                        size="small"
                        color="primary"
                        onClick={() => handleEditUser(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => handleViewUserDetails(item)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setItemsToDelete([item.id]);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Details Dialog */}
      <Dialog 
        open={userDetailsOpen} 
        onClose={() => setUserDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                  <Typography variant="body1">{selectedUser.full_name || selectedUser.first_name || "N/A"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedUser.email || "N/A"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedUser.phone || selectedUser.phone_number || "N/A"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Account Status</Typography>
                  <Chip
                    label={selectedUser.status === 1 ? "Active" : selectedUser.status === 0 ? "Inactive" : selectedUser.account_status || "N/A"}
                    color={
                      selectedUser.status === 1 ? "success" :
                      selectedUser.status === 0 ? "error" :
                      selectedUser.account_status === "active" ? "success" :
                      selectedUser.account_status === "pending" ? "warning" :
                      selectedUser.account_status === "suspended" ? "error" : "default"
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Account Balance</Typography>
                  <Typography variant="body1" fontWeight="bold">{getAccountBalance(selectedUser)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total Performance</Typography>
                  <Typography variant="body1" color="primary">{getPerformancePercentage(selectedUser)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total Sum ($)</Typography>
                  <Typography variant="body1" color="primary">{getTotalSumInDollars(selectedUser)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                  <Typography variant="body1">{dayjs(selectedUser.created_at).format("DD/MM/YYYY HH:mm")}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Document ID</Typography>
                  <Typography variant="body1">{selectedUser.doc_id}</Typography>
                </Grid>
              </Grid>
              
              {/* Monthly Closings Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Monthly Closings</Typography>
                {selectedUser.monthlyClosings && selectedUser.monthlyClosings.length > 0 ? (
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Month/Year</TableCell>
                          <TableCell>Performance %</TableCell>
                          <TableCell>Closing Balance</TableCell>
                          <TableCell>Profit/Loss</TableCell>
                          <TableCell>Admin Commission</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Email Notified</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedUser.monthlyClosings.map((closing, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {closing.closing_month !== undefined && closing.closing_year !== undefined 
                                ? `${closing.closing_month + 1}/${closing.closing_year}` 
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                {closing.user_performance !== undefined ? `${closing.user_performance.toFixed(2)}%` : 
                                 closing.percent !== undefined ? `${closing.percent.toFixed(2)}%` : "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                ${closing.closing_balance !== undefined ? closing.closing_balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color={closing.profit_loss_bool ? "success.main" : "error.main"}>
                                ${closing.profit_loss !== undefined ? closing.profit_loss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                ${closing.admin_comission !== undefined ? closing.admin_comission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {closing.date ? dayjs(closing.date).format("DD/MM/YYYY HH:mm") : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={closing.is_email_notified ? "Yes" : "No"} 
                                color={closing.is_email_notified ? "success" : "default"}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No monthly closings found for this user.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {itemsToDelete.length} selected user(s)?
            This action cannot be undone and will permanently remove user data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={editingUser.full_name || ''}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      full_name: e.target.value
                    })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      email: e.target.value
                    })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      phone: e.target.value
                    })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Status"
                    select
                    value={editingUser.account_status || 'active'}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      account_status: e.target.value
                    })}
                    margin="normal"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Balance"
                    type="number"
                    value={editingUser.net_balance || 0}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      net_balance: parseFloat(e.target.value) || 0
                    })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Performance % (Monthly)"
                    type="number"
                    value={editingUser.user_performance || 0}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      user_performance: parseFloat(e.target.value) || 0
                    })}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveUserEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDataAnalysis;
